// @flow
import { useSelect } from "downshift";
import { css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCaretUp,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { isEqual } from "lodash";
import { media } from "@nested/brand";
import { FieldLabel } from "./ButtonGroup";

const wrapperStyle = css`
  position: relative;
`;

const buttonStyle = css`
  background-color: white;
  border: 1px solid
    ${({ $valid, theme }) =>
      $valid ? theme.palette.hague40 : theme.palette.terracotta150};
  border-radius: 5px;
  display: block;
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  line-height: 14px;
  font-size: 14px;
  height: 42px;
  font-weight: 500;
  cursor: pointer;
  ${({ $open }) =>
    $open &&
    css`
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
    `}
  ${media.tablet`
     height: 50px;
     font-size: 16px;
  `}
`;

const valueStyle = css`
  padding: 10px;
  color: ${({ theme }) => theme.palette.hague100};
  flex-grow: 1;
  overflow: hidden;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const caretStyle = css`
  padding: 10px;
  color: ${({ theme }) => theme.palette.hague80};
  flex-grow: 0;
`;

export const listStyle = css`
  z-index: 10;
  list-style-type: none;
  width: 100%;
  position: absolute;
  top: 100%;
  left: 0;
  background-color: white;
  margin: 0;
  display: ${({ $open }) => ($open ? "block" : "none")};
  padding: 0;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  border: 1px solid ${({ theme }) => theme.palette.hague40};
  border-top: none;
  outline: none;
  max-height: 200px;
  overflow-y: auto;
`;

export const listItemStyle = css`
  padding: 10px 10px;
  font-size: 14px;
  line-height: 17px;
  font-weight: ${({ $selected }) => ($selected ? "500" : "400")};
  border: 1px solid transparent;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.palette.hague100};

  ${({ $highlight }) =>
    $highlight &&
    css`
      background: ${({ theme }) => theme.palette.blue15};
    `};

  ${({ $selected }) =>
    $selected &&
    css`
      font-weight: 500;
      color: ${({ theme }) => theme.palette.hague100};
    `};

  ${media.tablet`
    line-height: 24px;
    font-size: 16px;
  `}
`;

const listItemLabelStyle = css`
  padding-right: 5px;
  overflow: hidden;
  max-width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const tickStyles = css`
  color: ${({ theme }) => theme.palette.green500};
  float: right;
`;

export type Props<T> = {
  options: {
    disabled?: boolean,
    label: string,
    value: T,
  }[],
  value: T,
  onChange(value: T): any,
  className?: string,
  "data-test"?: string,
  label?: string,
  valid?: boolean,
  placeholder?: string,
};

export const Select = <T>({
  options,
  value,
  onChange,
  className,
  label,
  valid = true,
  placeholder = null,
  "data-test": dataTest = "select-field",
}: Props<T>) => {
  const selectedItem =
    options.find((option) => isEqual(option.value, value)) || null;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    selectedItem,
    items: options,
    itemToString: ({ label: itemLabel }) => itemLabel,
    onSelectedItemChange: (changes) =>
      onChange(changes.selectedItem?.value || null),
  });

  return (
    <div data-test={dataTest} css={wrapperStyle} className={className}>
      {label && <FieldLabel $valid={valid}>{label}</FieldLabel>}
      <button
        $open={isOpen}
        {...getToggleButtonProps()}
        css={buttonStyle}
        $valid={valid}
        data-test={`${dataTest}:button`}
        type="button"
      >
        <span css={valueStyle} data-test={`${dataTest}:value`}>
          {selectedItem?.label || placeholder}
        </span>
        <span css={caretStyle}>
          <FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} />
        </span>
      </button>
      <ul $open={isOpen} css={listStyle} {...getMenuProps()}>
        {options.map((item, index) => {
          const selected = selectedItem?.value === item.value;
          return (
            <li
              css={listItemStyle}
              $highlight={highlightedIndex === index}
              $selected={selected}
              key={item.value}
              data-test={`${dataTest}:option:${String(item.value)}`}
              {...getItemProps({ item, index })}
            >
              <span css={listItemLabelStyle}>{item.label}</span>
              {selected && <FontAwesomeIcon icon={faCheck} css={tickStyles} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
